/**
 * The adapter between the wire and `@annwn/ui` for the character sheet (RC2-03).
 *
 * The sheet is the milestone's flagship library surface, and like every other
 * island it is fed already-derived view models: the React overlay composes
 * `Tabs`, `BodyDiagram`, `Meter`, `Table` and `WoundLedger`, and holds no game
 * state. Everything with a rule in it — how a rep-count becomes a bar fraction,
 * how a satchel sorts into a categorised inventory, when a freshly-crossed rung
 * glows once and never again — lives here, pure and tested, on the client side of
 * the M6-03 boundary the library must never cross.
 *
 * Numbers keep the discipline the design set. Familiarity progress arrives from
 * the server already worded (`phrase`) with a bar fraction (`fill`) and never as
 * reps; attributes are the one sanctioned number (design spec §7); the inventory
 * shows a count and a weight, the two a person knows about their own bag (M2-06).
 */

import { ITEMS, PREPARATIONS, item } from '@annwn/content';
import type { AttributeSnapshot, FamiliaritySnapshot, SelfSnapshot } from '@annwn/protocol';
import type { BodyPart, WoundSummary } from '@annwn/ui';

import { toBodyParts, toWoundLedger } from './condition-view-model.js';
import { freshnessLabel, satchelView, type SatchelLine } from './satchel-pane.js';

/* -------------------------------------------------------------------------- *
 * The once-only "getting better" glow (AC-3)
 * -------------------------------------------------------------------------- */

const VETERAN_KEY = 'annwn.veteran';
const SEEN_KEY = 'annwn.seen-rungs';

/** The sliver of `localStorage` this needs, so a test can hand it a plain map. */
export interface RungStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

function rungKey(character: string, familiarity: string, band: string): string {
  return `${character}\u0000${familiarity}\u0000${band}`;
}

/**
 * Which rungs a player has already been shown crossing — persisted, per
 * character, so the "you're getting better at X" glow fires exactly once and
 * never again (RC2-03 AC-3, the M7-03 once-only discipline).
 *
 * The seasoned player who has seen it all can silence the beat with a veteran
 * toggle (`annwn.veteran`), and then no rung ever glows — the same courtesy the
 * coaching hints extend. `untried` never glows: not having done a thing is not a
 * thing getting better.
 *
 * Marking is deferred to `remember`, which the overlay calls when the sheet
 * closes, so a rung crossed this session glows for the whole of the viewing it
 * was crossed in — not for a single reconciled frame — and is quiet forever
 * after.
 */
export class RungMemory {
  private readonly store: RungStore | null;
  private readonly veteran: boolean;
  private readonly seen: Set<string>;

  constructor(store: RungStore | null) {
    this.store = store;
    this.veteran = store?.getItem(VETERAN_KEY) === 'on';
    this.seen = new Set(parseSeen(store?.getItem(SEEN_KEY) ?? null));
  }

  /** Should this rung glow — an earned band this character has not been shown yet? */
  isFresh(character: string, rung: FamiliaritySnapshot): boolean {
    if (this.veteran || rung.band === 'untried') return false;
    return !this.seen.has(rungKey(character, rung.familiarity, rung.band));
  }

  /** Commit every earned rung on show, so none of them glows a second time. */
  remember(character: string, rungs: readonly FamiliaritySnapshot[]): void {
    let changed = false;
    for (const rung of rungs) {
      if (rung.band === 'untried') continue;
      const key = rungKey(character, rung.familiarity, rung.band);
      if (!this.seen.has(key)) {
        this.seen.add(key);
        changed = true;
      }
    }
    if (changed && this.store) this.store.setItem(SEEN_KEY, JSON.stringify([...this.seen]));
  }
}

function parseSeen(raw: string | null): string[] {
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter((one): one is string => typeof one === 'string')
      : [];
  } catch {
    // A mangled store is not worth a crash on a cosmetic glow — start it fresh.
    return [];
  }
}

/* -------------------------------------------------------------------------- *
 * The view model
 * -------------------------------------------------------------------------- */

/** One familiarity row, as the Character tab renders it. */
export interface FamiliarityRow {
  readonly familiarity: string;
  readonly label: string;
  /** The rank word, or the door line ("needs an axe") when locked. */
  readonly phrase: string;
  /** 0..1, for the `Meter` fill. A ratio, never shown as a number. */
  readonly fill: number;
  readonly locked: boolean;
  /** Glows awen this viewing — a rung freshly crossed (AC-3). */
  readonly fresh: boolean;
}

/** One categorised group of what you carry, for the Equipment inventory table. */
export interface InventoryGroup {
  readonly category: string;
  readonly lines: readonly SatchelLine[];
}

export type CraftVerb = 'build' | 'cook' | 'prepare';

export interface CraftRecipeView {
  readonly id: string;
  readonly result: string;
  readonly ingredients: readonly string[];
  readonly ready: boolean;
  readonly kind: 'cook' | 'prepare';
}

export interface CraftActionView {
  readonly verb: CraftVerb;
  readonly label: string;
  readonly available: boolean;
  readonly reason: string | null;
}

export interface CharacterSheetView {
  readonly identity: {
    readonly name: string;
    readonly trade: string | null;
    readonly blurb: string | null;
  };
  readonly attributes: readonly AttributeSnapshot[];
  readonly familiarities: readonly FamiliarityRow[];
  /** The raw wire rungs, for the overlay to `remember` on close. */
  readonly rungs: readonly FamiliaritySnapshot[];
  readonly parts: readonly BodyPart[];
  readonly wounds: readonly WoundSummary[];
  readonly figureCaption: string;
  readonly heldWeapon: string | null;
  readonly heldWeaponId: string | null;
  readonly heldAmmunition: number | null;
  readonly heldWeaponStowable: boolean;
  readonly inventory: readonly InventoryGroup[];
  readonly inventoryWeight: string;
  readonly inventoryEmpty: string | null;
  readonly crafting: {
    readonly recipes: readonly CraftRecipeView[];
    readonly actions: readonly CraftActionView[];
  };
  readonly errands: readonly {
    readonly id: string;
    readonly prompt: string;
    readonly giver: string;
    readonly objectives: SelfSnapshot['sheet']['errands'][number]['objectives'];
  }[];
}

/**
 * The five categories the inventory sorts into, in listing order — derived from
 * flags the satchel already carries (M2-06), so nothing new had to reach the
 * wire. Order is deliberate: what keeps you alive first, keepsakes last.
 */
const CATEGORY_ORDER = ['Provisions', 'Remedies', 'Keepsakes', 'Other'] as const;

function categoryOf(
  item: SelfSnapshot['satchel']['items'][number],
): (typeof CATEGORY_ORDER)[number] {
  if (item.remedy) return 'Remedies';
  if (item.edible) return 'Provisions';
  if (item.provenance) return 'Keepsakes';
  return 'Other';
}

/**
 * The whole sheet, derived from one self snapshot.
 *
 * The paper-doll, its wounds and the inventory are read off `combat` and
 * `satchel` — the same data the condition overlay and the satchel window already
 * show — through the tested adapters, so the sheet can never disagree with them
 * about a wound or a weight.
 */
export function characterSheetView(
  self: SelfSnapshot,
  tick: number,
  memory: RungMemory,
): CharacterSheetView {
  const { sheet } = self;
  const character = sheet.identity.name;

  const familiarities: FamiliarityRow[] = sheet.familiarities.map((rung) => ({
    familiarity: rung.familiarity,
    label: rung.label,
    phrase: rung.phrase,
    fill: rung.fill,
    locked: rung.locked,
    fresh: memory.isFresh(character, rung),
  }));

  const combat = self.combat;
  const parts = combat ? toBodyParts(combat.parts) : [];
  const wounds = combat ? toWoundLedger(combat.wounds, combat.parts, tick) : [];

  const view = satchelView(self.satchel);
  const byCategory = new Map<string, SatchelLine[]>();
  for (const item of self.satchel.items) {
    const line = view.lines.find((one) => one.defId === item.defId);
    if (!line) continue;
    const category = categoryOf(item);
    const bucket = byCategory.get(category) ?? [];
    bucket.push(line);
    byCategory.set(category, bucket);
  }
  const inventory: InventoryGroup[] = CATEGORY_ORDER.filter((category) =>
    byCategory.has(category),
  ).map((category) => ({ category, lines: byCategory.get(category) ?? [] }));

  const carried = new Map(self.satchel.items.map((one) => [one.defId, one.count]));
  const has = (id: string): boolean => (carried.get(id) ?? 0) > 0;
  const displayName = (id: string): string =>
    self.satchel.items.find((one) => one.defId === id)?.name ?? item(id).name;

  const preparations: CraftRecipeView[] = PREPARATIONS.map((recipe, index) => {
    const ingredientIds = [recipe.herbId, ...(recipe.withId ? [recipe.withId] : [])];
    return {
      id: `prepare:${recipe.id}:${recipe.withId ?? 'fire'}:${index}`,
      result: item(recipe.id).name,
      ingredients: ingredientIds.map(displayName),
      ready: ingredientIds.every(has),
      kind: 'prepare',
    };
  });
  const cooking: CraftRecipeView[] = ITEMS.filter(
    (one) => one.tags.includes('raw') || one.tags.includes('roastable'),
  ).flatMap((ingredient) => {
    const resultId = ingredient.tags.includes('raw')
      ? ingredient.id.replace(/^raw-/, 'cooked-')
      : `roasted-${ingredient.id}`;
    try {
      return [
        {
          id: `cook:${ingredient.id}`,
          result: item(resultId).name,
          ingredients: [displayName(ingredient.id)],
          ready: has(ingredient.id),
          kind: 'cook' as const,
        },
      ];
    } catch {
      return [];
    }
  });
  const actionLabel: Readonly<Record<CraftVerb, string>> = {
    build: 'Lay a fire',
    cook: 'Cook what you carry',
    prepare: 'Make up a remedy',
  };
  const actions: CraftActionView[] = (['build', 'cook', 'prepare'] as const).map((verb) => {
    const offer = self.affordances.find((one) => one.verb === verb);
    return {
      verb,
      label: actionLabel[verb],
      available: offer?.available ?? false,
      reason: offer?.available ? (offer.note ?? null) : (offer?.reason ?? 'not available here'),
    };
  });

  return {
    identity: sheet.identity,
    attributes: sheet.attributes,
    familiarities,
    rungs: sheet.familiarities,
    parts,
    wounds,
    figureCaption: figureCaption(wounds),
    heldWeapon: combat?.weapon?.stowable ? combat.weapon.name : null,
    heldWeaponId: combat?.weapon?.stowable ? combat.weapon.id : null,
    heldAmmunition: combat?.weapon?.stowable ? (combat.weapon.arrows ?? null) : null,
    heldWeaponStowable: combat?.weapon?.stowable ?? false,
    inventory,
    inventoryWeight: view.weight,
    inventoryEmpty: view.empty,
    crafting: { recipes: [...preparations, ...cooking], actions },
    errands: sheet.errands.map((one) => ({
      id: one.id,
      prompt: one.prompt,
      giver: one.giver,
      objectives: one.objectives,
    })),
  };
}

/**
 * The line under the paper-doll — sound, or the worst thing wrong with you.
 *
 * A sentence, not a tally: the wound ledger beside it carries the list, so the
 * caption's job is the glance — "sound and whole", or the one wound your eye
 * should go to first.
 */
export function figureCaption(wounds: readonly WoundSummary[]): string {
  if (wounds.length === 0) return 'Sound and whole.';
  const worst = wounds.reduce((a, b) => (severityRank(b.tone) > severityRank(a.tone) ? b : a));
  return `Sound, but for ${worst.severity} at the ${worst.part.toLowerCase()}.`;
}

const TONE_RANK = ['bruised', 'light', 'deep', 'severe', 'crippling', 'mortal'] as const;
function severityRank(tone: WoundSummary['tone']): number {
  const index = tone ? (TONE_RANK as readonly string[]).indexOf(tone) : -1;
  return index < 0 ? 0 : index;
}

export { freshnessLabel };
